import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { UploadCloud, Loader2, Cloud, ShieldCheck, FolderUp, FilePlus2, Trash2, HardDrive, RotateCcw, Zap, Server } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { StagedGroup } from "@/components/upload/StagedGroup";
import { AUDIO_EXT, fromDataTransfer, fromInput, splitFiles } from "@/lib/collectFiles";
import { api, formatApiError } from "@/lib/api";
import { StorageHealthBadge } from "@/components/StorageHealthBadge";
import { fingerprint, findResumable, forgetUpload, rememberUpload, runPool } from "@/lib/resumable";
import { useAuth } from "@/context/AuthContext";
import { fmtBytes } from "@/components/OneDriveQuota";
import { usageTone } from "@/components/StoragePill";

const Notice = ({ testid, title, text, children }) => (
  <div className="flex flex-col items-start gap-4 rounded-lg border border-zinc-200 bg-white p-8" data-testid={testid}>
    <div className="flex h-10 w-10 items-center justify-center rounded-md bg-sky-50 text-sky-700"><Cloud size={18} /></div>
    <div><p className="text-lg font-medium">{title}</p><p className="mt-1 max-w-xl text-sm text-zinc-500">{text}</p></div>
    {children}
  </div>
);

let seq = 0;
const FIELDS = ["title", "artist", "album", "album_artist", "track_no"];

async function inspect(file) {
  const fd = new FormData();
  fd.append("file", file.slice(0, 1024 * 1024));
  fd.append("filename", file.name);
  fd.append("size", file.size);
  const { data } = await api.post("/onedrive/inspect", fd);
  return { title: data.title, artist: data.artist, album: data.album, album_artist: data.album_artist, track_no: data.track_no ?? "" };
}

const DEST = { filejump: { label: "FileJump", icon: Server }, app: { label: "Resonance storage", icon: HardDrive }, onedrive: { label: "OneDrive", icon: Cloud } };
const BASE = { filejump: "/webdav", app: "/storage", onedrive: "/onedrive" };

const UsageBar = ({ usage }) => {
  if (!usage) return null;
  const pool = !usage.limit && usage.pool;
  const used = pool ? usage.pool.used : usage.used, limit = pool ? usage.pool.limit : usage.limit;
  const { pct, tone } = usageTone(used, limit);
  return (
    <div className="min-w-56 flex-1" data-testid="storage-usage">
      <p className={`text-xs ${pct >= 90 ? "font-medium text-red-700" : "text-zinc-600"}`} data-testid="storage-usage-text">
        {fmtBytes(used)} used{limit ? ` of ${fmtBytes(limit)}` : " · no limit"}{pool ? " · all accounts" : ""}{pct >= 90 ? " · almost full" : ""}
      </p>
      {limit && <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-zinc-100"><div className={`h-full rounded-full ${tone}`} style={{ width: `${pct}%` }} /></div>}
    </div>
  );
};

export default function Upload() {
  const { user } = useAuth();
  const perms = user?.permissions || {};
  const [dest, setDest] = useState(perms.filejump ? "filejump" : "app");
  const [usage, setUsage] = useState(null);
  const [od, setOd] = useState(null);
  const [fj, setFj] = useState(null);
  const [folderId, setFolderId] = useState("");
  const [organize, setOrganize] = useState(true);
  const [items, setItems] = useState([]);
  const [covers, setCovers] = useState({});
  const [coverStatus, setCoverStatus] = useState({});
  const [drag, setDrag] = useState(false);
  const [running, setRunning] = useState(false);
  const filesRef = useRef(null);
  const dirRef = useRef(null);
  const directOk = useRef(true);

  const ok = (k) => (k === "app" ? perms.upload_app : k === "filejump" ? perms.filejump : perms.upload_onedrive && od?.connected && od.folders?.length > 0);
  const choices = Object.keys(DEST).filter(ok);
  const loading = (perms.filejump && !fj) || (perms.upload_onedrive && !od);
  useEffect(() => {
    if (perms.filejump) api.get("/webdav").then((r) => setFj(r.data)).catch(() => setFj({ connected: false }));
    if (perms.upload_onedrive) api.get("/onedrive").then((r) => setOd(r.data)).catch(() => setOd({ connected: false }));
  }, [perms.filejump, perms.upload_onedrive]);
  useEffect(() => {
    if (!loading && !ok(dest) && choices.length) setDest(choices[0]);
  }, [choices.join(","), dest, loading]); // eslint-disable-line react-hooks/exhaustive-deps
  const loadUsage = () => api.get("/storage/usage").then((r) => setUsage(r.data)).catch(() => {});
  useEffect(() => { if (dest === "app") loadUsage(); }, [dest]);
  useEffect(() => {
    const ids = (od?.folders || []).map((f) => f.id);
    if (!ids.includes(folderId)) setFolderId(ids[0] || "");
  }, [od, folderId]);

  const patch = (key, p) => setItems((arr) => arr.map((x) => (x.key === key ? { ...x, ...p } : x)));
  const editFields = (key, p) => setItems((arr) => arr.map((x) => {
    if (x.key !== key) return x;
    const syncAA = p.artist !== undefined && (!x.fields.album_artist || x.fields.album_artist === x.fields.artist);
    return { ...x, fields: { ...x.fields, ...p, ...(syncAA && { album_artist: p.artist }) } };
  }));
  const editGroup = (dir, p) => setItems((arr) => arr.map((x) => (x.dir === dir && x.status === "ready" ? { ...x, fields: { ...x.fields, ...p } } : x)));
  const remove = (key) => setItems((arr) => arr.filter((x) => x.key !== key));

  const stage = async (list) => {
    const { audio, covers: found } = splitFiles(list);
    if (!audio.length) return toast.error("No supported audio files found");
    const staged = audio.map(({ file, dir }) => ({ key: `f${++seq}`, file, dir, status: "reading", progress: 0, fields: {}, orig: {} }));
    setItems((arr) => [...arr, ...staged]);
    setCovers((c) => ({ ...c, ...found }));
    const queue = [...staged];
    const worker = async () => {
      for (let it = queue.shift(); it; it = queue.shift()) {
        try {
          const f = await inspect(it.file);
          patch(it.key, { status: "ready", fields: f, orig: f });
        } catch (e) {
          const f = { title: it.file.name.replace(/\.[^.]+$/, ""), artist: "", album: "", album_artist: "", track_no: "" };
          patch(it.key, { status: "ready", fields: f, orig: {} });
        }
        const r = await findResumable(it.file, BASE[dest]);
        if (r) patch(it.key, { resume: Math.round((r.received.length / r.total_chunks) * 100) });
      }
    };
    await Promise.all([worker(), worker(), worker(), worker()]);
  };

  const CHUNK = 4 * 1024 * 1024;
  const PARALLEL = 4;
  const putChunk = async (base, i, blob, onProgress) => {
    for (let attempt = 0; ; attempt++) {
      try {
        return await api.put(`${base}/chunk/${i}`, blob, { headers: { "Content-Type": "application/octet-stream" }, onUploadProgress: onProgress });
      } catch (e) {
        if (attempt >= 2 || (e.response && e.response.status < 500)) throw e;
        await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
      }
    }
  };
  const uploadChunked = async (it, fd, base) => {
    const fp = fingerprint(it.file, base);
    let up = await findResumable(it.file, base);
    if (up) toast(`Resuming ${it.file.name} — ${Math.round((up.received.length / up.total_chunks) * 100)}% was already uploaded`);
    else {
      const { data: b } = await api.post(`${base}/uploads/begin`, { filename: it.file.name, size: it.file.size, chunk_size: CHUNK });
      up = { upload_id: b.upload_id, total_chunks: b.total_chunks, chunk_size: b.chunk_size, received: [] };
      rememberUpload(fp, { upload_id: b.upload_id, base });
    }
    const done = new Set(up.received);
    const loaded = {};
    done.forEach((i) => { loaded[i] = Math.min(up.chunk_size, it.file.size - i * up.chunk_size); });
    const report = () => {
      const sent = Object.values(loaded).reduce((a, b) => a + b, 0);
      patch(it.key, { progress: Math.min(99, Math.round((sent / it.file.size) * 100)) });
    };
    const jobs = [];
    for (let i = 0; i < up.total_chunks; i++) {
      if (done.has(i)) continue;
      jobs.push(async () => {
        const blob = it.file.slice(i * up.chunk_size, Math.min((i + 1) * up.chunk_size, it.file.size));
        await putChunk(`${base}/uploads/${up.upload_id}`, i, blob, (e) => { loaded[i] = Math.min(e.loaded, blob.size); report(); });
        loaded[i] = blob.size;
        report();
      });
    }
    report();
    await runPool(jobs, PARALLEL);
    patch(it.key, { progress: 100 });
    try {
      const { data } = await api.post(`${base}/uploads/${up.upload_id}/finish`, fd);
      return data;
    } finally {
      forgetUpload(fp);
    }
  };

  const putDirect = (url, blob, range, onProgress) => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Range", range);
    xhr.upload.onprogress = (e) => onProgress(e.loaded);
    xhr.onload = () => resolve({ status: xhr.status, body: xhr.responseText });
    xhr.onerror = () => reject(Object.assign(new Error("direct upload blocked"), { direct: true }));
    xhr.send(blob);
  });
  const uploadDirect = async (it) => {
    const meta = { filename: it.file.name, size: it.file.size, folder_id: folderId, organize, subdir: it.dir };
    FIELDS.forEach((k) => { meta[k] = String(it.fields[k] ?? ""); });
    const { data: b } = await api.post("/onedrive/direct/begin", meta);
    let item = null;
    let sent = 0;
    for (let off = 0; off < it.file.size; off += b.chunk_size) {
      const end = Math.min(off + b.chunk_size, it.file.size);
      const blob = it.file.slice(off, end);
      let res;
      for (let attempt = 0; ; attempt++) {
        res = await putDirect(b.upload_url, blob, `bytes ${off}-${end - 1}/${it.file.size}`,
          (loaded) => patch(it.key, { progress: Math.min(99, Math.round(((sent + loaded) / it.file.size) * 100)) }));
        if (res.status < 400) break;
        if (attempt >= 2 || (res.status < 500 && res.status !== 429)) throw new Error(`OneDrive rejected the upload (${res.status})`);
        await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
      }
      sent = end;
      if (res.status === 200 || res.status === 201) item = JSON.parse(res.body);
    }
    if (!item?.id) throw new Error("OneDrive did not confirm the upload");
    patch(it.key, { progress: 100 });
    const { data } = await api.post("/onedrive/direct/finish", { item_id: item.id, ...Object.fromEntries(FIELDS.map((k) => [k, String(it.fields[k] ?? "")])) });
    return data;
  };

  const uploadOne = async (it) => {
    const fd = new FormData();
    if (dest === "onedrive") {
      fd.append("folder_id", folderId);
      fd.append("organize", organize);
      fd.append("subdir", it.dir);
    }
    const edited = FIELDS.some((k) => String(it.fields[k] ?? "") !== String(it.orig[k] ?? ""));
    FIELDS.forEach((k) => { if (String(it.fields[k] ?? "") !== String(it.orig[k] ?? "")) fd.append(k, it.fields[k] ?? ""); });
    patch(it.key, { status: "uploading", progress: 0 });
    let data;
    if (dest === "onedrive" && !edited && directOk.current) {
      try {
        data = await uploadDirect(it);
      } catch (e) {
        if (!e.direct) throw e;
        directOk.current = false;
        toast("Direct upload to OneDrive isn't available here — sending via the server instead");
        patch(it.key, { progress: 0 });
        data = await uploadChunked(it, fd, "/onedrive");
      }
    } else {
      data = await uploadChunked(it, fd, BASE[dest]);
    }
    patch(it.key, { status: "done", progress: 100, result: data });
    return data;
  };

  const uploadCover = async (dir, result) => {
    const fd = new FormData();
    fd.append("file", covers[dir]);
    if (dest === "onedrive") {
      fd.append("folder_id", folderId);
      fd.append("organize", organize);
      fd.append("subdir", dir);
    }
    fd.append("album", result.album);
    fd.append("album_artist", result.album_artist);
    setCoverStatus((s) => ({ ...s, [dir]: "uploading" }));
    try {
      await api.post(`${BASE[dest]}/upload-cover`, fd);
      setCoverStatus((s) => ({ ...s, [dir]: "done" }));
    } catch (e) {
      setCoverStatus((s) => ({ ...s, [dir]: "error" }));
      toast.error(`Cover for ${dir}: ${formatApiError(e)}`);
    }
  };

  const runUpload = async (pending) => {
    setRunning(true);
    let found = 0;
    const byDir = {};
    for (const it of pending) {
      try {
        const data = await uploadOne(it);
        found += data.wishlist_found || 0;
        byDir[it.dir] ||= data;
      } catch (e) {
        patch(it.key, { status: "error", error: formatApiError(e) });
      }
    }
    for (const [dir, result] of Object.entries(byDir)) {
      if (covers[dir] && coverStatus[dir] !== "done") await uploadCover(dir, result);
    }
    setRunning(false);
    if (dest === "app") loadUsage();
    if (found) toast.success(`Found ${found} wishlist song${found > 1 ? "s" : ""} — ticked off automatically`);
  };
  const uploadAll = () => runUpload(items.filter((x) => x.status === "ready" || x.status === "error"));
  const retryFailed = () => runUpload(items.filter((x) => x.status === "error"));
  const retryOne = (it) => runUpload([it]);

  const groups = useMemo(() => {
    const g = {};
    items.forEach((it) => { (g[it.dir] ||= []).push(it); });
    return Object.entries(g);
  }, [items]);

  const reconnect = async () => {
    try { const { data } = await api.get("/onedrive/login"); window.location.href = data.url; } catch (e) { toast.error(formatApiError(e)); }
  };
  const header = <PageHeader eyebrow="Tools" title="Upload music" testid="upload-title" />;
  const section = null;
  const picker = choices.length > 0 && (
    <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="upload-destination">
      <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">Upload to</span>
      <div className="flex rounded-full border border-zinc-200 bg-white p-1">
        {choices.map((k) => {
          const Icon = DEST[k].icon;
          return (
            <button key={k} onClick={() => !running && setDest(k)} disabled={running} data-testid={`upload-dest-${k}`}
              className={`flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${dest === k ? "bg-zinc-900 text-white" : "text-zinc-600 hover:text-zinc-900"}`}>
              <Icon size={13} />{DEST[k].label}
            </button>
          );
        })}
      </div>
    </div>
  );
  const wrap = (body) => <div className="max-w-5xl">{header}{section}{picker}{body && <div className="mt-6">{body}</div>}</div>;
  if (!choices.length) return wrap(loading ? null : <Notice testid="upload-disabled" title="Uploading is turned off" text="Your account can stream and sync music, but uploading isn't enabled. Ask the owner to turn it on in Settings → Accounts & permissions." />);
  if (!choices.includes(dest) || loading) return wrap(null);
  if (dest === "filejump" && !fj.connected) return wrap(<Notice testid="upload-filejump-not-connected" title="Connect FileJump to upload" text="FileJump is your primary storage. Connect it once in Settings — uploads then go straight into your FileJump Music folder."><Link to="/settings" className="flex items-center gap-2 rounded-full bg-zinc-900 px-5 py-2 text-sm text-white" data-testid="upload-filejump-settings-link"><Server size={14} />Open Settings</Link></Notice>);
  if (dest === "onedrive" && (!od.can_write || od.needs_reconnect)) return wrap(<Notice testid="upload-needs-write" title="Allow uploads to OneDrive" text="Resonance currently has read-only access. Reconnect once and approve “edit your files” so new songs can be saved into your OneDrive music folder."><button onClick={reconnect} className="flex items-center gap-2 rounded-full bg-zinc-900 px-5 py-2 text-sm text-white" data-testid="upload-reconnect-button"><ShieldCheck size={14} />Reconnect OneDrive</button></Notice>);

  const ready = items.filter((x) => x.status === "ready" || x.status === "error").length;
  const reading = items.some((x) => x.status === "reading");
  const done = items.filter((x) => x.status === "done").length;
  const failed = items.filter((x) => x.status === "error").length;
  return wrap(
    <>
      <div className="mb-6 flex flex-wrap items-center gap-6 rounded-lg border border-zinc-200 bg-white px-6 py-4">
        {dest === "filejump" ? (
          <div className="max-w-xl" data-testid="upload-filejump-note">
            <p className="text-xs text-zinc-500">Songs are saved to FileJump as <span className="font-mono">{fj.root}/Artist/Album/NN - Title</span> and appear in your library right away. Edited details are written into the file. Guests see them only after you grant FileJump access.</p>
            {fj.quota_used != null && <p className="mt-1 text-[11px] text-zinc-400" data-testid="upload-filejump-quota">{fmtBytes(fj.quota_used)} used{fj.quota_available != null ? ` of ${fmtBytes(fj.quota_used + fj.quota_available)}` : ""} on FileJump</p>}
          </div>
        ) : dest === "app" ? (
          <>
            <div className="max-w-md">
              <p className="text-xs text-zinc-500">Songs are stored privately in Resonance's own storage — free for everyone. Edited details are saved into the file.</p>
              <div className="mt-1.5"><StorageHealthBadge /></div>
            </div>
            <UsageBar usage={usage} />
          </>
        ) : (<>
        <div className="flex items-center gap-3">
          <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">Save to</span>
          <Select value={folderId} onValueChange={setFolderId} disabled={running}>
            <SelectTrigger className="w-64 font-mono text-xs" data-testid="upload-folder-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {od.folders.map((f) => <SelectItem key={f.id} value={f.id} data-testid={`upload-folder-option-${f.id}`} className="font-mono text-xs">{f.path || "/"}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <label className="flex items-center gap-3 text-sm text-zinc-600">
          <Switch checked={organize} onCheckedChange={setOrganize} disabled={running} data-testid="upload-organize-switch" />
          {organize ? <>Sort into <span className="font-mono text-xs">Artist/Album/</span> folders</> : <>Keep my folder names</>}
        </label>
        <p className="basis-full text-xs text-zinc-500" data-testid="direct-upload-note">
          <Zap size={11} className="mr-1 inline -mt-0.5 text-amber-600" />Files go straight from your browser to OneDrive — songs with edited details take the server route so the new tags can be written into the file.
        </p>
        </>)}
      </div>
      <div
        data-testid="upload-drag-drop-zone"
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={async (e) => { e.preventDefault(); setDrag(false); if (!running) stage(await fromDataTransfer(e.dataTransfer)); }}
        className={`flex flex-col items-start gap-5 rounded-lg border-2 border-dashed px-10 py-12 transition-colors duration-200 ${drag ? "border-amber-500 bg-amber-50" : "border-zinc-300 bg-white/60"}`}>
        <UploadCloud size={36} strokeWidth={1.25} className={drag ? "text-amber-600" : "text-zinc-400"} />
        <div>
          <p className="font-heading text-2xl font-semibold">Drop songs or whole album folders here</p>
          <p className="mt-1 text-sm text-zinc-500">MP3, FLAC, M4A, AAC, OGG, OPUS, WAV — up to 300MB each. A cover.jpg / folder.jpg inside a folder is saved alongside the songs. Check and fix titles before saving.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => filesRef.current?.click()} disabled={running} data-testid="upload-choose-files-button" className="flex h-10 items-center gap-2 rounded-full border border-zinc-300 bg-white px-5 text-sm hover:border-zinc-900 disabled:opacity-40"><FilePlus2 size={14} />Choose files</button>
          <button onClick={() => dirRef.current?.click()} disabled={running} data-testid="upload-choose-folder-button" className="flex h-10 items-center gap-2 rounded-full border border-zinc-300 bg-white px-5 text-sm hover:border-zinc-900 disabled:opacity-40"><FolderUp size={14} />Choose folder</button>
        </div>
        <input ref={filesRef} type="file" multiple accept={AUDIO_EXT.map((e) => `.${e}`).join(",")} className="hidden"
          data-testid="upload-file-input" onChange={(e) => { stage(fromInput(e.target.files)); e.target.value = ""; }} />
        <input ref={dirRef} type="file" multiple webkitdirectory="" directory="" className="hidden"
          data-testid="upload-folder-input" onChange={(e) => { stage(fromInput(e.target.files)); e.target.value = ""; }} />
      </div>
      {items.length > 0 && (
        <div className="mt-10">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-zinc-600" data-testid="upload-summary">
              {done} of {items.length} saved to {DEST[dest].label} {(running || reading) && <Loader2 size={13} className="ml-1 inline animate-spin" />}
            </p>
            <div className="flex items-center gap-3">
              {done > 0 && !running && <Link to="/albums?sort=recent" className="text-sm underline-offset-4 hover:underline" data-testid="upload-view-library">View in library →</Link>}
              {!running && <button onClick={() => { setItems([]); setCovers({}); setCoverStatus({}); }} data-testid="upload-clear-button" className="flex h-10 items-center gap-1.5 px-3 text-sm text-zinc-500 hover:text-red-600"><Trash2 size={14} />Clear</button>}
              {failed > 0 && !running && (
                <button onClick={retryFailed} data-testid="upload-retry-failed-button" className="flex h-10 items-center gap-2 rounded-full border border-red-200 bg-white px-5 text-sm text-red-700 transition-colors hover:border-red-600 hover:bg-red-50">
                  <RotateCcw size={14} />Retry {failed} failed
                </button>
              )}
              <button onClick={uploadAll} disabled={running || reading || !ready} data-testid="upload-start-button"
                className="flex h-10 items-center gap-2 rounded-full bg-zinc-900 px-6 text-sm text-white transition-transform hover:scale-[1.02] disabled:opacity-40">
                {running ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />}Save {ready || ""} to {DEST[dest].label}
              </button>
            </div>
          </div>
          <div className="space-y-6">
            {groups.map(([dir, its], i) => (
              <StagedGroup key={dir || "_loose"} index={i} dir={dir} items={its} cover={covers[dir]} coverStatus={coverStatus[dir]}
                locked={running} onEdit={editFields} onEditGroup={editGroup} onRemove={remove} onRetry={retryOne} />
            ))}
          </div>
        </div>
      )}
    </>
  );
}
